import { useEffect, useState } from "react";
import { Download, KeyRound, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, ts } from "@/lib/api";
import { Panel, btn, field } from "@/components/ui-kit";

const HUB = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_FOLDER = "\\\\qb-server\\ftp\\EDI-GXS-SAP\\ORDER";
const DEFAULT_DLV = "\\\\qb-server\\ftp\\EDI-GXS-SAP\\DESADV";
const DEFAULT_INV = "\\\\qb-server\\ftp\\EDI-GXS-SAP\\INVOIC";

export const buildScript = (key, folder, dlv = "", inv = "") => `# EP EDI Order Hub - SAP IDoc pickup (Windows PowerShell 5.1+)
# 1) Downloads every new SAP order IDoc from the Hub into $Folder, then marks it collected.
# 2) Posts every SAP delivery IDoc (DELVRY03 / DESADV) in $DeliveryFolder and every SAP billing IDoc (INVOIC02) in
#    $InvoiceFolder to the Hub (943 / 856 / DESADV / INVOIC), then moves it to <folder>\\Processed (or \\Error).
# Run every 5 minutes with Task Scheduler (see the Hub: Order Import > SFTP & SAP status).

$Hub    = "${HUB}"
$Key    = "${key}"
$Folder = "${folder}"
$DeliveryFolder = "${dlv}"
$InvoiceFolder = "${inv}"
$Log    = Join-Path $PSScriptRoot "idoc-pickup.log"

[Net.ServicePointManager]::SecurityProtocol = [Net.SecurityProtocolType]::Tls12
$Headers = @{ "X-Download-Key" = $Key }
function Write-Log($msg) { "$(Get-Date -Format 'dd/MM/yyyy HH:mm:ss') $msg" | Add-Content -Path $Log }

try {
    if (-not (Test-Path $Folder)) { throw "Folder not reachable: $Folder" }
    $resp  = Invoke-RestMethod -Uri "$Hub/idoc-pickup/list" -Headers $Headers -Method Get
    $files = @($resp | ForEach-Object { $_ } | Where-Object { $_.filename })
    foreach ($f in $files) {
        $dest = Join-Path $Folder ([string]$f.filename)
        $tmp  = "$dest.part"
        if (Test-Path $dest) {
            Write-Log "SKIP $($f.filename) - already in folder, not overwritten"
        } else {
            Invoke-WebRequest -Uri "$Hub/idoc-pickup/$($f.id)" -Headers $Headers -OutFile $tmp -UseBasicParsing
            Rename-Item -Path $tmp -NewName ([string]$f.filename)
            Write-Log "OK   $($f.filename)"
        }
        Invoke-RestMethod -Uri "$Hub/idoc-pickup/$($f.id)/collected" -Headers $Headers -Method Post | Out-Null
    }
    if ($files.Count -eq 0) { Write-Log "No new IDoc files" }
} catch {
    Write-Log "ERROR $($_.Exception.Message)"
    $failed = $true
}

foreach ($InFolder in @($DeliveryFolder, $InvoiceFolder | Where-Object { $_ } | Select-Object -Unique)) {
    try {
        if (-not (Test-Path $InFolder)) { throw "Folder not reachable: $InFolder" }
        foreach ($sub in @("Processed", "Error")) {
            $p = Join-Path $InFolder $sub
            if (-not (Test-Path $p)) { New-Item -ItemType Directory -Path $p | Out-Null }
        }
        $dfiles = @(Get-ChildItem -Path $InFolder -File | Where-Object { $_.Name -notlike "*.part" })
        foreach ($df in $dfiles) {
            $text = [IO.File]::ReadAllText($df.FullName, [Text.Encoding]::GetEncoding(28591))
            $body = @{ filename = [string]$df.Name; content = $text } | ConvertTo-Json -Compress
            $r = Invoke-RestMethod -Uri "$Hub/idoc-pickup/deliveries" -Headers $Headers -Method Post -ContentType "application/json; charset=utf-8" -Body ([Text.Encoding]::UTF8.GetBytes($body))
            $sub = if ($r.error) { "Error" } else { "Processed" }
            Move-Item -Path $df.FullName -Destination (Join-Path (Join-Path $InFolder $sub) ([string]$df.Name)) -Force
            if ($r.error) { Write-Log "IN  ERROR $($df.Name) - $($r.error)" }
            foreach ($d in @($r.deliveries)) { Write-Log "DLV  $($df.Name) delivery $($d.delivery_no): $($d.status) $($d.message) $($d.error)" }
            foreach ($d in @($r.invoices)) { Write-Log "INV  $($df.Name) billing $($d.invoice_no) PO $($d.po_number): $($d.status) $($d.error)" }
        }
        if ($dfiles.Count -eq 0) { Write-Log "No new files in $InFolder" }
    } catch {
        Write-Log "IN  ERROR $InFolder $($_.Exception.Message)"
        $failed = $true
    }
}
if ($failed) { exit 1 }
`;

const save = (text) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text.replace(/\n/g, "\r\n")], { type: "text/plain" }));
  a.download = "idoc-pickup.ps1";
  a.click();
};

export const IdocPickupPanel = () => {
  const [st, setSt] = useState(null);
  const [key, setKey] = useState("");
  const [folder, setFolder] = useState(DEFAULT_FOLDER);
  const [dlv, setDlv] = useState(DEFAULT_DLV);
  const [inv, setInv] = useState(DEFAULT_INV);
  const load = () => api.get("/idoc-pickup/key").then((r) => setSt(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (st?.active && !window.confirm("Create a new key? The script already on your server stops working until you replace it with the new one.")) return;
    try {
      const { data } = await api.post("/idoc-pickup/key");
      setKey(data.key);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke the key? The script stops downloading and IDoc files go back to the SAP SFTP (if set up).")) return;
    await api.delete("/idoc-pickup/key").catch((e) => toast.error(errMsg(e)));
    setKey("");
    load();
  };
  if (!st) return null;
  return (
    <Panel title="Download IDoc files to your file server" testId="idoc-pickup-panel">
      <div className="space-y-3 p-5 text-sm">
        <p className="text-slate-400">A PowerShell script on your Windows server collects each new SAP order IDoc from the Hub and saves it to your folder. Each file is downloaded once and marked <span className="text-slate-200">collected</span>. While a key is active the Hub does not also send IDoc files to the SAP SFTP. Files held in test mode are not collected until you click Send to SAP.</p>
        <div className="flex flex-wrap items-center gap-3" data-testid="idoc-pickup-status">
          <span className={st.active ? "text-green-300" : "text-slate-400"}>{st.active ? `Key active - created ${ts(st.created_at)} by ${st.created_by}` : "No key - script not set up"}</span>
          {st.active && <span className="text-slate-500">Last pickup: {st.last_used ? ts(st.last_used) : "never"}</span>}
          <span className="text-slate-500">Waiting to collect: {st.waiting}</span>
        </div>
        <label className="block max-w-xl"><span className="mb-1 block font-mono text-[10px] uppercase text-slate-500">Save files to</span>
          <Input value={folder} onChange={(e) => setFolder(e.target.value)} className={field} data-testid="idoc-pickup-folder" /></label>
        <label className="block max-w-xl"><span className="mb-1 block font-mono text-[10px] uppercase text-slate-500">SAP delivery IDocs (DELVRY03) to send - leave blank to skip</span>
          <Input value={dlv} onChange={(e) => setDlv(e.target.value)} className={field} data-testid="idoc-pickup-delivery-folder" /></label>
        <label className="block max-w-xl"><span className="mb-1 block font-mono text-[10px] uppercase text-slate-500">SAP billing IDocs (INVOIC02) to send - leave blank to skip</span>
          <Input value={inv} onChange={(e) => setInv(e.target.value)} className={field} data-testid="idoc-pickup-invoice-folder" /></label>
        <div className="flex flex-wrap gap-2">
          <Button onClick={create} className={btn.primary} data-testid="idoc-pickup-create-key"><KeyRound className="mr-1.5 h-4 w-4" /> {st.active ? "Create new key and script" : "Create key and script"}</Button>
          {st.active && <Button variant="ghost" onClick={revoke} className={btn.danger} data-testid="idoc-pickup-revoke"><ShieldOff className="mr-1.5 h-4 w-4" /> Revoke key</Button>}
        </div>
        {key && (
          <div className="space-y-2 rounded-sm border border-amber-500/40 bg-amber-500/10 p-3" data-testid="idoc-pickup-new-key">
            <p className="text-amber-200">Download the script now - the key is only shown once.</p>
            <code className="block break-all font-mono text-xs text-slate-200" data-testid="idoc-pickup-key-text">{key}</code>
            <Button onClick={() => save(buildScript(key, folder, dlv, inv))} className={btn.primary} data-testid="idoc-pickup-download-script"><Download className="mr-1.5 h-4 w-4" /> Download idoc-pickup.ps1</Button>
          </div>
        )}
        <div className="text-xs text-slate-400" data-testid="idoc-pickup-steps">
          <p className="mb-1 font-semibold text-slate-300">Set up on the server</p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>Copy idoc-pickup.ps1 to e.g. C:\EPHub\ on the server.</li>
            <li>Test it once: open PowerShell and run <code className="text-slate-200">powershell -ExecutionPolicy Bypass -File C:\EPHub\idoc-pickup.ps1</code>, then check C:\EPHub\idoc-pickup.log.</li>
            <li>Schedule it every 5 minutes (run as an account that can write to the folder): <code className="break-all text-slate-200">schtasks /Create /TN "EP Hub IDoc pickup" /TR "powershell.exe -NoProfile -ExecutionPolicy Bypass -File C:\EPHub\idoc-pickup.ps1" /SC MINUTE /MO 5 /RU DOMAIN\serviceaccount /RP *</code></li>
          </ol>
        </div>
      </div>
    </Panel>
  );
};
